import { type MouseEvent, type UIEvent, useCallback, useMemo, useState } from "react";

import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { AlternateTopboxRow } from "./AlternateTopboxRow";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  isNullish,
  Modal,
  Table,
  Text
} from "@theorgchartdev/storybook";
import { GetDoc } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { appstr } from "@coreapp/utils/appstr";
import { TABLE_SELECTED_ROW_STYLE } from "@/Utils/Constants";

const PAGE_SIZE = 25;

export const AlternateTopboxModal = () => {
  const [allTopBoxes, setAllTopBoxes] = useState<Record<string, string>[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedTopBoxId, setSelectedTopBoxId] = useState<Nullable<string>>(null);

  useTopic("AlternateTopboxes", (topBoxes: Record<string, string>[]) => {
    setSelectedTopBoxId(null);
    setAllTopBoxes(topBoxes);
    setVisibleCount(PAGE_SIZE);
  });

  const visibleTopBoxes = useMemo(
    () => allTopBoxes.slice(0, visibleCount),
    [allTopBoxes, visibleCount]
  );

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (visibleCount >= allTopBoxes.length) return;

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= 40) {
        setVisibleCount(prev => Math.min(prev + PAGE_SIZE, allTopBoxes.length));
      }
    },
    [visibleCount, allTopBoxes.length]
  );

  if (allTopBoxes.length === 0) {
    return null;
  }

  const switchToTopbox = (topBoxId: string) => {
    const doc = GetDoc();
    doc?.DoChartDataQuery(topBoxId);
  };

  const handleClose = () => {
    setAllTopBoxes([]);
  };

  const handleApply = () => {
    switchToTopbox(selectedTopBoxId!);
    handleClose();
  };

  const handleRowClick = (event: MouseEvent, data: Record<string, string>) => {
    const doc = GetDoc();
    const objDB = doc?.GetObjDB();
    const boxClass = objDB?.GetClassByName("Box");
    const personIdDef = boxClass?.GetFieldDefByID("PersonID");
    if (!personIdDef) return;

    const personIDField = personIdDef.GetFieldName();
    const recordId = data[personIDField];

    switch (event.detail) {
      case 1:
        setSelectedTopBoxId(recordId);
        break;
      case 2:
        switchToTopbox(recordId);
        handleClose();
        break;
      default:
    }
  };

  const doc = GetDoc();
  const objDB = doc?.GetObjDB();
  const boxClass = objDB?.GetClassByName("Box");
  if (!boxClass) return null;

  const personIdDef = boxClass.GetRecordIDFieldDef();
  const nameDef = boxClass.GetFieldDefByID("Name");
  const titleDef = boxClass.GetTitleFieldDef();
  const headcountDef = boxClass.GetFieldDefByID("Headcount");
  if (!personIdDef || !nameDef || !titleDef || !headcountDef) return null;

  const personIDField = personIdDef.GetFieldName();
  const titleField = titleDef.GetFieldName();
  const headcountField = headcountDef.GetFieldName();

  const personIDLabel = personIdDef.GetFieldLabel();
  const nameLabel = nameDef.GetFieldLabel();
  const titleLabel = titleDef.GetFieldLabel();
  const headcountLabel = headcountDef.GetFieldLabel();

  const columns = [
    {
      field: "label",
      headerName: `${nameLabel} (${personIDLabel})`,
      type: "cellRenderer",
      columnWidth: "fifty",
      CellRenderer: AlternateTopboxRow
    },
    {
      field: titleField,
      headerName: titleLabel
    },
    {
      field: headcountField,
      headerName: headcountLabel
    }
  ];

  const gridOptions = {
    getRowStyle: (data: Record<string, string>, _rowIndex: number, isHovered: boolean) => {
      if (selectedTopBoxId === data[personIDField]) {
        return TABLE_SELECTED_ROW_STYLE;
      } else if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: handleRowClick
  };

  return (
    <CentralizedModalContainer show>
      <Modal
        id="alt-topbox-modal"
        minWidth="medium"
        maxWidth="large"
        show
        centerScreen
        onClose={handleClose}
      >
        <DialogTitle divider={true} onClose={handleClose}>
          {appstr("Select a new topbox")}
        </DialogTitle>
        <DialogContent>
          <Flex direction="column" gap="6px" padding="6px 0 0 0">
            <Text size="small">{appstr("Click on a topbox from the list below")}</Text>
            <Flex direction="column" gap="2px" padding="10px 0 0 0">
              <Text colorModifier="grey" size="small">
                {appstr("Showing X of Y managers", {
                  visibleCount: visibleTopBoxes.length.toLocaleString(),
                  totalCount: allTopBoxes.length.toLocaleString()
                })}
              </Text>
            </Flex>
            <div style={{ maxHeight: "400px", overflowY: "auto" }} onScroll={handleScroll}>
              <Table
                tableLayout="fixed"
                rowSize="largeR"
                textSize="mediumT"
                columns={columns}
                gridOptions={gridOptions}
                data={visibleTopBoxes}
              />
            </div>
          </Flex>
        </DialogContent>
        <DialogActions>
          <DialogActions.Primary disabled={isNullish(selectedTopBoxId)} onClick={handleApply}>
            {appstr("Select")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
